/**
 * The era kit — sixth-century Wales, as data (AoD §9.1 reshaped; M2-04).
 *
 * What a person in Cwm Twrch can get their hands on, and what each one is for.
 * The spread is the design: this is a century in which the *spear* is the
 * weapon, because it is cheap and long, and everything else is either the knife
 * you already own or a statement about how much land your family holds. A sword
 * is not a better axe; it is a grave-good, and the only ones in the valley came
 * out of somebody's barrow.
 *
 * `damageRating` is the `±n` of AoD's `ST±n` for anything muscle-powered: a
 * spear is `+1`, meaning *one better than the arm behind it*. Hazards set
 * `strengthScaled: false` and give the rating outright, because a crag does not
 * care how strong you are.
 *
 * Timing is in sim ticks at ten a second (M2-01). The numbers have to make each
 * weapon a different decision rather than a different number: a knife cycles
 * twice a second and cannot get through mail; a Dane axe takes most of a second
 * and a half to come round, telegraphs the whole way, needs both hands, and ends
 * the argument when it arrives.
 *
 * Reach is where the era actually lives. The spear fights at two tiles and loses
 * three dice inside its own shaft, and everything about a dark-ages line follows
 * from those two numbers.
 *
 * **Not here yet:** the sling. It is in M2-04's table as a six-tile arc, and a
 * six-tile arc is a ranged combat system — line of fire, a penalty structure
 * that does not read "inside your own reach", an AI that knows to close — not a
 * row of data. Deferred whole rather than shipped as a stone you club people
 * with at arm's length. See the ticket.
 */
export const FISTS = {
    id: 'fists',
    name: 'bare hands',
    description: 'Knuckles and the flat of the hand. Always to hand, and never enough — a ' +
        'brawl in the yard ends in bruises and hurt pride, which is rather the point.',
    damageType: 'blunt',
    damageRating: -2,
    strengthScaled: true,
    profileId: 'bash',
    timing: { windUp: 2, recovery: 2, reach: 1, insidePenalty: 0, staminaCost: 1 },
};
export const STAFF = {
    id: 'staff',
    name: 'a staff',
    description: 'A length of ash, waist-thick at the butt and worn smooth in the middle. ' +
        'The poor man’s spear: it keeps a man at two paces and turns what it cannot ' +
        'avoid, and it is the only weapon in the valley nobody can be arrested for.',
    damageType: 'blunt',
    damageRating: 0,
    strengthScaled: true,
    profileId: 'bash',
    twoHanded: true,
    // Its whole argument. A staff is worth more in the hands than on the target.
    defenceDice: 1,
    timing: { windUp: 4, recovery: 3, reach: 2, insidePenalty: 2, staminaCost: 2 },
};
export const KNIFE = {
    id: 'knife',
    name: 'a knife',
    description: 'Four inches of iron with an antler grip, for eating, for whittling, and ' +
        'for the last resort. Everybody has one. Almost nobody wins a fight with one.',
    damageType: 'cut',
    damageRating: -1,
    strengthScaled: true,
    profileId: 'swing',
    takesEdge: true,
    // Everybody's knife is also the valley's only surgical instrument (M4-05).
    uses: ['fine'],
    timing: { windUp: 3, recovery: 2, reach: 1, insidePenalty: 0, staminaCost: 1 },
};
export const SEAX = {
    id: 'seax',
    name: 'a seax',
    description: 'A single-edged blade the length of a forearm, with the broken back that ' +
        'gives it its name. The sidearm of the age: quick, cheap enough for a free ' +
        'man, and useless against a well-made byrnie.',
    damageType: 'cut',
    damageRating: 0,
    strengthScaled: true,
    profileId: 'swing',
    takesEdge: true,
    // Long enough to take a limb off, fine enough to dig for a splinter.
    uses: ['fine', 'cleave'],
    timing: { windUp: 4, recovery: 3, reach: 1, insidePenalty: 0, staminaCost: 2 },
};
export const SPEAR = {
    id: 'spear',
    name: 'a spear',
    description: 'A leaf-shaped head socketed onto seven feet of ash. The weapon of the ' +
        'century and of every century either side of it: cheap, long, and lethal to ' +
        'a man who has let you keep your distance.',
    damageType: 'puncture',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'thrust',
    takesEdge: true,
    timing: { windUp: 4, recovery: 4, reach: 2, insidePenalty: 3, staminaCost: 2 },
};
/**
 * RC2's one ranged weapon.  It is a straight, deliberate shot — not the
 * deferred sling arc — and stays out of starting loadouts as an earned tool.
 */
export const BOW = {
    id: 'bow',
    name: 'a yew bow',
    description: 'Yew bent slow over a knee, its belly dark with use. The arrows are plain ash shafts, ' +
        'fletched with goose feathers and made to be aimed, not thrown away in a hurry.',
    damageType: 'puncture',
    damageRating: 0,
    strengthScaled: true,
    profileId: 'shot',
    twoHanded: true,
    ranged: { range: 6, falloffDice: 1 },
    timing: { windUp: 6, recovery: 5, reach: 1, insidePenalty: 0, staminaCost: 2 },
};
export const AXE = {
    id: 'axe',
    name: 'a bearded axe',
    description: 'A working axe with the lower edge drawn down into a beard, so it hooks as ' +
        'well as it cuts. It splits firewood six days a week. On the seventh it is ' +
        'the reason a shield is a thing that wears out.',
    damageType: 'cut',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'swing',
    takesEdge: true,
    // What an amputation is actually done with in this century (M4-05).
    uses: ['cleave'],
    timing: { windUp: 6, recovery: 5, reach: 1, insidePenalty: 0, staminaCost: 3 },
};
/**
 * A sword. Rare loot rather than shop stock, and never worth more damage than
 * the axe beside it — what a sword buys is speed and the hand it leaves free,
 * not a bigger number. Balance is the whole reason a sword was worth a farm.
 */
export const SWORD = {
    id: 'sword',
    name: 'a sword',
    description: 'Pattern-welded, two spans of it, with a garnet in the pommel and somebody ' +
        'else’s name worked into the blade. Not shop stock and never was: this came ' +
        'out of a barrow, or off a man who did not get up.',
    damageType: 'cut',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'swing',
    takesEdge: true,
    timing: { windUp: 4, recovery: 3, reach: 1, insidePenalty: 0, staminaCost: 2 },
};
/** Two hands, no shield, and nothing survives being caught by it. */
export const DANE_AXE = {
    id: 'dane-axe',
    name: 'a Dane axe',
    description: 'A hand-and-a-half of edge on a five-foot haft, meant for taking the front ' +
        'rank apart. It needs both hands and most of a second and a half, and the ' +
        'man holding it has decided he does not need a shield.',
    damageType: 'cut',
    damageRating: 2,
    strengthScaled: true,
    profileId: 'swing',
    twoHanded: true,
    takesEdge: true,
    // Ruinous, and it will do.
    uses: ['cleave'],
    timing: { windUp: 8, recovery: 6, reach: 1, insidePenalty: 0, staminaCost: 4 },
};
/** A wolf's jaws are a weapon its body swings; they scale with its frame. */
export const WOLF_JAWS = {
    id: 'wolf.jaws',
    name: 'its teeth',
    description: 'Forty-two of them, and a jaw built to hold a running deer.',
    damageType: 'bite',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'bite',
    timing: { windUp: 3, recovery: 3, reach: 1, insidePenalty: 0, staminaCost: 2 },
};
/** A stag rears and comes down. Not a bite, not a gore — a hammer, twice. */
export const STAG_HOOVES = {
    id: 'stag.hooves',
    name: 'its forehooves',
    description: 'Two hooves the size of a fist, at the top of a rear, coming down.',
    damageType: 'blunt',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'strike',
    timing: { windUp: 5, recovery: 4, reach: 1, insidePenalty: 0, staminaCost: 3 },
};
/** Twelve points of bone swept sideways. It throws people rather than killing them. */
export const STAG_ANTLERS = {
    id: 'stag.antlers',
    name: 'its antlers',
    description: 'A twelve-point head of antler, four feet across, swung with the whole ' +
        'neck behind it. It is meant for other stags, and it does not know that.',
    damageType: 'puncture',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'toss',
    timing: { windUp: 6, recovery: 5, reach: 1, insidePenalty: 0, staminaCost: 3 },
};
/**
 * A rabbit's teeth (M3-10). It has to have something, and this is honestly it.
 *
 * Rating 0 rather than 1, and that is a statement rather than a rounding: a
 * cornered rabbit can draw blood off a hand and cannot do anything a person
 * needs to worry about. It is here so that the one animal in the valley that
 * cannot hurt you still goes through exactly the same fight rules as the one
 * that can — a creature with no weapon would have been a special case in the
 * resolver, and the special case is how a whole species quietly stops working.
 */
export const RABBIT_TEETH = {
    id: 'rabbit.teeth',
    name: 'its teeth',
    description: 'Two chisels at the front of a head the size of a fist. It would rather run.',
    damageType: 'bite',
    damageRating: 0,
    strengthScaled: true,
    profileId: 'bite',
    timing: { windUp: 3, recovery: 3, reach: 1, insidePenalty: 0, staminaCost: 1 },
};
export const BOAR_TUSKS = {
    id: 'boar.tusks',
    name: 'its tusks',
    description: 'Two curved blades of ivory kept honed against the uppers, coming up from ' +
        'below at the height of a man’s knee.',
    damageType: 'puncture',
    damageRating: 2,
    strengthScaled: true,
    profileId: 'gore',
    timing: { windUp: 5, recovery: 4, reach: 1, insidePenalty: 0, staminaCost: 3 },
};
/**
 * Ysgithyrwyn's tusks (M5-04) — the pair the *Culhwch* names him for.
 *
 * A wild boar's gore turned up: one more point of base damage, and a longer,
 * heavier stroke he cannot throw as often, which is the honest shape of a blow
 * from something the size of a pony. It is `strengthScaled` off a frame nothing
 * else in the valley has, so what actually arrives is a level or two above any
 * blade a hunter carries — the reason a man who is caught by it goes down and
 * frequently does not get up.
 *
 * The same `gore` profile as the wild boar, aimed low, so his wounds land in
 * the legs and the belly of whoever he catches — which is the pipeline's
 * business, and where the tusk-toss (`combat.ts`) reads its knockdown from.
 */
export const YSGITHYRWYN_TUSKS = {
    id: 'ysgithyrwyn.tusks',
    name: 'his tusks',
    description: 'Two yellowed sabres of ivory as long as a man’s forearm, worn to a chisel ' +
        'at the tips and grooved along their length where a hundred autumns of ' +
        'whetting against each other have shaped them.',
    damageType: 'puncture',
    damageRating: 3,
    strengthScaled: true,
    profileId: 'gore',
    // Slower than the wild boar's: a great deal of animal has to be swung to bring
    // them round, and the pause after is the window a party drills to exploit.
    timing: { windUp: 6, recovery: 6, reach: 1, insidePenalty: 0, staminaCost: 4 },
};
/**
 * A cow's horns (M5-01), and the reason a pen is not a petting zoo.
 *
 * Docile is not the same as harmless. A cow will not come for you and cannot be
 * provoked into it at range, but a beast you have backed into the corner of a
 * fold will hook sideways with a horn and the whole weight of its neck behind
 * it — which is `toss`, the same profile a stag's antlers use, because it is
 * mechanically the same event: a wide sweep that throws a man rather than
 * killing him. Rating 1 and hers only when `cornered` (see `beasts.ts`) is
 * exactly the shape of the real hazard.
 */
export const COW_HORNS = {
    id: 'cow.horns',
    name: 'its horns',
    description: 'Two short lyre horns on a skull like a stone, swung sideways by half a ' +
        'ton of animal that has decided it has nowhere left to go.',
    damageType: 'puncture',
    damageRating: 1,
    strengthScaled: true,
    profileId: 'toss',
    timing: { windUp: 7, recovery: 6, reach: 1, insidePenalty: 0, staminaCost: 3 },
};
/**
 * A ewe's head (M5-01). Rating 0, and honestly so.
 *
 * It exists for the reason the rabbit's teeth do: an animal with no weapon at
 * all would be a special case in the resolver, and the special case is how a
 * whole species quietly stops working. What a sheep can actually do to you is
 * knock you off your feet on a slope, and blunt 0 at that wind-up is that.
 */
export const SHEEP_BUTT = {
    id: 'sheep.butt',
    name: 'its head',
    description: 'A hard flat skull under a hand of wool, put into your thigh at a run.',
    damageType: 'blunt',
    damageRating: 0,
    strengthScaled: true,
    profileId: 'strike',
    timing: { windUp: 5, recovery: 5, reach: 1, insidePenalty: 0, staminaCost: 2 },
};
/**
 * Not a weapon: the ground. Its rating stands in for the height fallen, and
 * the caller rolls a severity in place of a contested margin. The pipeline
 * cannot tell the difference, which is the point of M1-03.
 */
export const HARD_GROUND = {
    id: 'hazard.fall',
    name: 'the fall',
    description: 'Rock, and rather a lot of air above it.',
    damageType: 'blunt',
    damageRating: 3,
    strengthScaled: false,
    profileId: 'fall',
};
export const HEARTH_FIRE = {
    id: 'hazard.fire',
    name: 'the fire',
    description: 'The hearth, banked and glowing, and hotter than it looks.',
    damageType: 'burn',
    damageRating: 2,
    strengthScaled: false,
    profileId: 'fire',
};
const WEAPONS = [
    FISTS,
    STAFF,
    KNIFE,
    SEAX,
    SPEAR,
    BOW,
    AXE,
    SWORD,
    DANE_AXE,
    WOLF_JAWS,
    STAG_HOOVES,
    STAG_ANTLERS,
    BOAR_TUSKS,
    YSGITHYRWYN_TUSKS,
    RABBIT_TEETH,
    COW_HORNS,
    SHEEP_BUTT,
    HARD_GROUND,
    HEARTH_FIRE,
];
export const WEAPON_KIT = new Map(WEAPONS.map((weapon) => [weapon.id, weapon]));
export function weapon(id) {
    const found = WEAPON_KIT.get(id);
    if (!found)
        throw new Error(`unknown weapon '${id}'`);
    return found;
}
//# sourceMappingURL=weapons.js.map